import React from "react";
import Image from "mui-image";
import Typography from "@mui/material/Typography";
import { useParams } from "react-router-dom";
import {
  usePublicGame,
  usePublicGameFiles,
  usePublicGameInstructions,
  usePublicProfile,
  useTrackPublicGame,
  useTrackPublicGameFiles,
} from "@seabrookstudios/pitch2table";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { EmptyDetailCardWithLogin } from "../core/EmptyDetailCard";
import Card from "@mui/material/Card";
import { PublicGameCardCover } from "../games/CoverImages";
import Stack from "@mui/material/Stack";
import { formatAge, formatDesigners } from "@seabrookstudios/pitch2table-core";
import { formatDuration, formatPlayerCount } from "../games/formatters";
import { PublicSizzle } from "../sizzle/UploadedSizzle";
import Chip from "@mui/material/Chip";
import { PublicSiteFrame } from "./PublicSiteFrame";
import { ClickToRevealEmailButton } from "./ClickToReveal";
import { PublicFile, PublicListEntry } from "../files/ListEntry";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import { PopUpMessages } from "../message-board/AddTopic";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import { useDocumentTitle } from "usehooks-ts";
import Avatar from "@mui/material/Avatar";
import Person from "@mui/icons-material/Person";
import logo from "../assets/logo.png";
import { useInvokeOnceWhenNotOwn } from "./useIsDesigner";

const PrivateProfile = () => {
  return (
    <PublicSiteFrame>
      <EmptyDetailCardWithLogin text="This designer profile is private (or the account does not or no longer exists). Please contact the designer directly." />
    </PublicSiteFrame>
  );
};

const PrivateGame = () => {
  return (
    <PublicSiteFrame>
      <EmptyDetailCardWithLogin text="This game is private (or is no longer accessible using this url). Please contact the designer directly." />
    </PublicSiteFrame>
  );
};

/**
 * @param {object} props
 * @param {string} props.username
 * @param {string} props.slug
 * @param {string} props.pnp
 * @param {import("@seabrookstudios/pitch2table-core").PublicGame} props.game
 */
const GamePnpFiles = ({ game, username, slug, pnp }) => {
  const { data: files = [] } = usePublicGameFiles(username, slug, pnp);
  const { data: messages = [] } = usePublicGameInstructions(username, slug, pnp);
  const track = useTrackPublicGameFiles(username, slug, pnp);
  useInvokeOnceWhenNotOwn(track, username);

  useDocumentTitle(game.name);

  return (
    <Box paddingTop={4}>
      <Stack direction="row" alignItems="center">
        <Typography variant="h6">Print and Play</Typography>
        <PopUpMessages messages={messages} tooltip="Print and Play instructions" />
      </Stack>
      {files.map((file) => (
        <>
          <PublicListEntry key={file.assetUrl} item={file} game={game} />
          <Divider />
        </>
      ))}
      {files.length === 0 && <Alert severity="info">No PNP files found for game. Please contact designer.</Alert>}
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublicGame} props.game
 */
const GameDesignerLogo = ({ game }) => {
  if (!game.designers || game.designers.length === 0) {
    return null;
  }

  if (!game.atLeastOneMember) {
    return (
      <Box display="flex" justifyContent="center" pb={2}>
        <Image alt="Pitch2Table" src={logo} fit="contain" width={64} height={64} />
      </Box>
    );
  }

  return (
    <Stack direction="row" pb={2} justifyContent="center" spacing={2}>
      {game.designers.map((designer) => {
        const designerName = `${designer.firstName} ${designer.lastName}`;

        return (
          <Box display="flex" justifyContent="center" pb={2} key={designerName}>
            {designer.avatar ? (
              <Avatar src={designer.avatar} alt={designerName} sx={{ width: 64, height: 64 }} />
            ) : (
              <Avatar sx={{ width: 64, height: 64 }}>
                <Person fontSize="large" />
              </Avatar>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublicGame} props.game
 */
const GameAwards = ({ game }) => {
  if (game.awards.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h6">Awards</Typography>
      <List>
        {game.awards.map((award, index) => (
          <ListItem key={`award-${index}`}>
            <ListItemIcon>{award.year}</ListItemIcon>
            <ListItemText primary={award.name} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {string} props.username
 * @param {string} props.slug
 * @param {string} props.pnp
 * @param {boolean} props.showPnp
 */
const PublicGameView = ({ username, slug, pnp, showPnp = false }) => {
  const { data: profile, isLoading } = usePublicProfile(username);
  const { data: game, isLoading: isLoadingGame } = usePublicGame(username, slug);
  const track = useTrackPublicGame(username, slug);
  useInvokeOnceWhenNotOwn(track, username);

  if (!profile || isLoading || isLoadingGame || !game) {
    return <CircularProgress />;
  }

  if (!profile.isPublic) {
    return <PrivateProfile />;
  }

  if (game.visibility === "Private") {
    return <PrivateGame />;
  }

  return (
    <PublicSiteFrame>
      <Card>
        <Box justifyContent="center" display="flex">
          <Box maxWidth="66ch">
            <Box padding={2}>
              <Stack direction="column" justifyContent="center">
                <Box justifyContent="center" display="flex" flexDirection="column">
                  <Typography align="center" variant="h3">
                    {game.name}
                  </Typography>
                  <Typography align="center" variant="subtitle1">
                    Designed by {game.customAuthors || formatDesigners(game.designers)}
                  </Typography>
                </Box>
                <Box p={2} />
                <Stack direction="row" justifyContent="space-evenly">
                  <Chip label={`Players: ${formatPlayerCount(game.playerCount)}`} />
                  <Chip label={`Age: ${formatAge(game.age)}`} />
                  <Chip label={`Duration: ${formatDuration(game.duration)}`} />
                </Stack>
                <Box p={2} />
                <PublicGameCardCover game={game} />
                <Box p={2} />
                <Typography align="center" variant="h5">
                  {game.tag}
                </Typography>
                <Box p={2} />
                <Typography>{game.description}</Typography>
                <Box p={2} />
                <GameAwards game={game} />
                <Box p={2} />
                {game.sizzle && <PublicSizzle url={game.sizzle} />}
                <Box p={4} />
              </Stack>
              {profile.showEmail && (
                <Box>
                  <Typography variant="h6">
                    {game.designers.length === 1 ? "Contact designer:" : "Contact designers:"}
                  </Typography>
                  <ClickToRevealEmailButton username={username} subject={game.name} />
                </Box>
              )}
              {(game.rulesAsset || game.sellSheetAsset) && (
                <Box paddingTop={4}>
                  {game.rulesAsset && (
                    <PublicFile
                      assetUrl={game.rulesAsset.assetUrl}
                      downloadUrl={game.rulesAsset.downloadUrl}
                      download={`${game.name} - rules.pdf`}
                      mimeType={game.rulesAsset.mimeType || ""}
                      name="Rules"
                      bytes={game.rulesAsset.bytes}
                    />
                  )}
                  {game.sellSheetAsset && (
                    <PublicFile
                      assetUrl={game.sellSheetAsset.assetUrl}
                      downloadUrl={game.sellSheetAsset.downloadUrl}
                      download={`${game.name} - sell sheet`}
                      mimeType={game.sellSheetAsset.mimeType || ""}
                      name="Sellsheet"
                      bytes={game.sellSheetAsset.bytes}
                    />
                  )}
                </Box>
              )}
              {showPnp && <GamePnpFiles game={game} username={username} slug={slug} pnp={pnp} />}
              <Box p={4} />
              <GameDesignerLogo game={game} />
            </Box>
          </Box>
        </Box>
      </Card>
    </PublicSiteFrame>
  );
};

export const PublicGameFromRoute = () => {
  const username = useParams().username;
  const slug = useParams().slug;
  const pnp = useParams().pnp;

  return <PublicGameView username={username || ""} slug={slug || ""} pnp={pnp || ""} showPnp={!!pnp} />;
};
